import { OptionType, ReasonType, SideEnum, WeightEnum } from '@/state/reducers'

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let current = ''

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  }
  if (current) lines.push(current)
  return lines
}

async function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

// All content is drawn in a 1080px-wide coordinate space.
// layoutMul scales fonts and spacings to fill fixed-size canvases vertically.
const CANVAS_WIDTH = 1080
// Auto keeps narrow columns so short reasons don't leave excessive horizontal space.
const H_PADDING_AUTO = 200
// Fixed-size exports fill the canvas width.
const H_PADDING_FILL = 60

const SECTION_LETTER_SPACING = 1.5

// Per-format layout constants, derived from a single multiplier.
interface Lc {
  titleFont: string
  optionFont: string
  sectionFont: string
  itemFont: string
  itemFontBold: string
  footerFont: string
  titleLineH: number
  optionLineH: number
  sectionHeaderH: number
  itemLineH: number
  itemBottomM: number
  bulletOff: number
  bulletIndent: number
  vPadding: number
  cardPadding: number
  cardGap: number
  colGap: number
  footerH: number
  footerGap: number
  titleBottomGap: number
  optionTitleGap: number
  dividerGapBars: number
  barH: number
  afterBarGap: number
  dividerGapNoBars: number
  sectionGap: number
  belowItemsGap: number
  logoSize: number
  logoTextGap: number
}

function buildLc(m: number): Lc {
  const r = (n: number) => Math.round(n * m)
  return {
    titleFont: `bold ${r(28)}px Georgia, serif`,
    optionFont: `bold ${r(20)}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`,
    sectionFont: `bold ${r(13)}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`,
    itemFont: `${r(15)}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`,
    itemFontBold: `bold ${r(15)}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`,
    footerFont: `${r(13)}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`,
    titleLineH: r(36),
    optionLineH: r(28),
    sectionHeaderH: r(24),
    itemLineH: r(22),
    itemBottomM: r(4),
    bulletOff: r(8),
    bulletIndent: r(16),
    vPadding: r(40),
    cardPadding: r(20),
    cardGap: r(16),
    colGap: r(20),
    footerH: r(44),
    footerGap: r(24),
    titleBottomGap: r(24),
    optionTitleGap: r(12),
    dividerGapBars: r(8),
    barH: r(8),
    afterBarGap: r(10),
    dividerGapNoBars: r(12),
    sectionGap: r(12),
    belowItemsGap: r(12),
    logoSize: r(20),
    logoTextGap: r(8),
  }
}

export type ImageSizeId =
  | 'auto'
  | 'square'
  | 'portrait'
  | 'vertical'
  | 'landscape'

interface SizeConfig {
  canvasW: number
  canvasH: number | null
  safeTop: number
  safeBottom: number
  // 9:16 uses stacked (pros above cons) to better use the tall format
  layout: 'two-column' | 'stacked'
  // scales fonts and spacing so content fills the canvas vertically
  layoutMul: number
}

const SIZE_CONFIGS: Record<ImageSizeId, SizeConfig> = {
  auto: {
    canvasW: CANVAS_WIDTH,
    canvasH: null,
    safeTop: 0,
    safeBottom: 0,
    layout: 'two-column',
    layoutMul: 1.0,
  },
  square: {
    canvasW: 1080,
    canvasH: 1080,
    safeTop: 0,
    safeBottom: 0,
    layout: 'two-column',
    layoutMul: 1.5,
  },
  portrait: {
    canvasW: 1080,
    canvasH: 1350,
    safeTop: 0,
    safeBottom: 0,
    layout: 'two-column',
    layoutMul: 1.9,
  },
  // Safe-area values follow Instagram/Facebook guidelines: 14% top, 20% bottom.
  vertical: {
    canvasW: 1080,
    canvasH: 1920,
    safeTop: Math.round(1920 * 0.14),
    safeBottom: Math.round(1920 * 0.2),
    layout: 'stacked',
    layoutMul: 1.55,
  },
  landscape: {
    canvasW: 1920,
    canvasH: 1080,
    safeTop: 0,
    safeBottom: 0,
    layout: 'two-column',
    layoutMul: 1.0,
  },
}

function measureColumnHeight(
  lc: Lc,
  ctx: CanvasRenderingContext2D,
  items: ReasonType[],
  colWidth: number,
): number {
  let h = lc.sectionHeaderH
  for (const item of items) {
    ctx.font = item.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
    const lines = wrapText(
      ctx,
      item.label,
      colWidth - lc.bulletOff - lc.bulletIndent,
    )
    h += lines.length * lc.itemLineH + lc.itemBottomM
  }
  return h
}

function measureCardHeight(
  lc: Lc,
  ctx: CanvasRenderingContext2D,
  option: OptionType,
  reasons: ReasonType[],
  cardWidth: number,
  includeBars: boolean,
): number {
  const textWidth = cardWidth - 2 * lc.cardPadding
  const colWidth = (textWidth - lc.colGap) / 2

  ctx.font = lc.optionFont
  const optionLines = wrapText(ctx, option.label, textWidth)
  let h =
    lc.cardPadding + optionLines.length * lc.optionLineH + lc.optionTitleGap

  h += includeBars
    ? 1 + lc.dividerGapBars + lc.barH + lc.afterBarGap
    : 1 + lc.dividerGapNoBars

  const optionReasons = reasons.filter((r) => r.optionId === option.id)
  const pros = optionReasons.filter((r) => r.side === SideEnum.PRO)
  const cons = optionReasons.filter((r) => r.side === SideEnum.CON)

  h +=
    Math.max(
      measureColumnHeight(lc, ctx, pros, colWidth),
      measureColumnHeight(lc, ctx, cons, colWidth),
    ) + lc.belowItemsGap

  h += lc.cardPadding
  return Math.max(h, 100)
}

// Stacked layout: pros section above cons section, each spanning full card width.
function measureCardHeightStacked(
  lc: Lc,
  ctx: CanvasRenderingContext2D,
  option: OptionType,
  reasons: ReasonType[],
  cardWidth: number,
  includeBars: boolean,
): number {
  const textWidth = cardWidth - 2 * lc.cardPadding

  ctx.font = lc.optionFont
  const optionLines = wrapText(ctx, option.label, textWidth)
  let h =
    lc.cardPadding + optionLines.length * lc.optionLineH + lc.optionTitleGap

  h += includeBars
    ? 1 + lc.dividerGapBars + lc.barH + lc.afterBarGap
    : 1 + lc.dividerGapNoBars

  const optionReasons = reasons.filter((r) => r.optionId === option.id)
  const pros = optionReasons.filter((r) => r.side === SideEnum.PRO)
  const cons = optionReasons.filter((r) => r.side === SideEnum.CON)

  if (pros.length > 0) {
    h += lc.sectionHeaderH
    for (const pro of pros) {
      ctx.font = pro.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
      const lines = wrapText(
        ctx,
        pro.label,
        textWidth - lc.bulletOff - lc.bulletIndent,
      )
      h += lines.length * lc.itemLineH + lc.itemBottomM
    }
  }

  if (cons.length > 0) {
    if (pros.length > 0) h += lc.sectionGap
    h += lc.sectionHeaderH
    for (const con of cons) {
      ctx.font = con.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
      const lines = wrapText(
        ctx,
        con.label,
        textWidth - lc.bulletOff - lc.bulletIndent,
      )
      h += lines.length * lc.itemLineH + lc.itemBottomM
    }
  }

  h += lc.cardPadding
  return Math.max(h, 80)
}

export async function generateAndDownloadImage(
  options: OptionType[],
  reasons: ReasonType[],
  fileName?: string,
  exportOptions?: {
    includeTitle?: boolean
    includeBars?: boolean
    imageSize?: ImageSizeId
  },
): Promise<void> {
  const includeTitle = exportOptions?.includeTitle ?? false
  const includeBars = exportOptions?.includeBars ?? true
  const sizeId: ImageSizeId = exportOptions?.imageSize ?? 'auto'
  const cfg = SIZE_CONFIGS[sizeId]
  const lc = buildLc(cfg.layoutMul)

  const logo = await loadImage('/logos/logo.png')

  // H_PADDING: narrow for auto, fill-width for social media sizes
  const hPadding = sizeId === 'auto' ? H_PADDING_AUTO : H_PADDING_FILL
  const cardWidth = CANVAS_WIDTH - 2 * hPadding

  const offscreen = document.createElement('canvas')
  offscreen.width = CANVAS_WIDTH
  offscreen.height = 100
  const measureCtx = offscreen.getContext('2d')
  if (!measureCtx) return

  const cardHeights =
    options.length > 0
      ? options.map((o) =>
          cfg.layout === 'stacked'
            ? measureCardHeightStacked(
                lc,
                measureCtx,
                o,
                reasons,
                cardWidth,
                includeBars,
              )
            : measureCardHeight(
                lc,
                measureCtx,
                o,
                reasons,
                cardWidth,
                includeBars,
              ),
        )
      : [100]

  measureCtx.font = lc.titleFont
  const titleLines =
    includeTitle && fileName ? wrapText(measureCtx, fileName, cardWidth) : []
  const titleHeight =
    titleLines.length > 0
      ? titleLines.length * lc.titleLineH + lc.titleBottomGap
      : 0

  const totalCardsHeight =
    cardHeights.reduce((a, b) => a + b, 0) +
    Math.max(0, cardHeights.length - 1) * lc.cardGap

  const naturalHeight =
    lc.vPadding +
    titleHeight +
    totalCardsHeight +
    lc.footerGap +
    lc.footerH +
    Math.round(lc.vPadding / 2)

  // --- Resolve canvas dimensions, scale, and offset ---
  //
  // Strategy: scale to fill the canvas width (landscape scales up 1.78×);
  // if height would overflow the available area, scale down further to fit.
  // Content is centered vertically within the safe area.
  // layoutMul pre-scales fonts/spacing so fixed-size formats fill their canvas.

  const canvasW = cfg.canvasW
  const canvasH = cfg.canvasH ?? naturalHeight
  const availableH = canvasH - cfg.safeTop - cfg.safeBottom

  // Start by filling the width
  let scale = canvasW / CANVAS_WIDTH
  // Pull back if the scaled content would overflow the available height
  if (naturalHeight * scale > availableH * 0.95) {
    scale = (availableH * 0.95) / naturalHeight
  }

  const scaledContentW = CANVAS_WIDTH * scale
  const scaledContentH = naturalHeight * scale

  // Center horizontally (matters for landscape where canvasW > CANVAS_WIDTH * scale)
  const offsetX = (canvasW - scaledContentW) / 2
  // Center vertically within the safe area
  const offsetY = cfg.safeTop + Math.max(0, (availableH - scaledContentH) / 2)

  // --- Draw ---
  const canvas = document.createElement('canvas')
  canvas.width = canvasW
  canvas.height = canvasH
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.fillStyle = '#f8f9f7'
  ctx.fillRect(0, 0, canvasW, canvasH)

  ctx.save()
  ctx.translate(offsetX, offsetY)
  ctx.scale(scale, scale)

  // Title
  let cursorY = lc.vPadding
  if (titleLines.length > 0) {
    ctx.fillStyle = '#0f172a'
    ctx.font = lc.titleFont
    for (const line of titleLines) {
      cursorY += lc.titleLineH
      ctx.fillText(line, hPadding, cursorY)
    }
    cursorY += lc.titleBottomGap
  }

  // Option cards
  options.forEach((option, i) => {
    const x = hPadding
    const y = cursorY
    const h = cardHeights[i]

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.05)'
    roundedRect(ctx, x + 2, y + 2, cardWidth, h, 8)
    ctx.fill()

    // Card background
    ctx.fillStyle = '#ffffff'
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    roundedRect(ctx, x, y, cardWidth, h, 8)
    ctx.fill()
    ctx.stroke()

    const textWidth = cardWidth - 2 * lc.cardPadding
    const leftX = x + lc.cardPadding

    let ty = y + lc.cardPadding

    // Option heading
    ctx.fillStyle = '#0f172a'
    ctx.font = lc.optionFont
    const optionLines = wrapText(ctx, option.label, textWidth)
    for (const line of optionLines) {
      ty += lc.optionLineH
      ctx.fillText(line, leftX, ty)
    }
    ty += lc.optionTitleGap

    // Horizontal divider
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(leftX, ty)
    ctx.lineTo(x + cardWidth - lc.cardPadding, ty)
    ctx.stroke()

    const optionReasons = reasons.filter((r) => r.optionId === option.id)
    const pros = optionReasons.filter((r) => r.side === SideEnum.PRO)
    const cons = optionReasons.filter((r) => r.side === SideEnum.CON)

    // Balance bar
    if (includeBars) {
      ty += lc.dividerGapBars
      const proWeight = pros.reduce((sum, r) => sum + r.weight + 1, 0)
      const conWeight = cons.reduce((sum, r) => sum + r.weight + 1, 0)
      const totalWeight = proWeight + conWeight
      if (totalWeight > 0) {
        const proPct = Math.round((proWeight / totalWeight) * 100)
        ctx.fillStyle = '#c0392b'
        ctx.fillRect(leftX, ty, textWidth, lc.barH)
        ctx.fillStyle = '#4a7c59'
        ctx.fillRect(leftX, ty, Math.round((textWidth * proPct) / 100), lc.barH)
      }
      ty += lc.barH + lc.afterBarGap
    } else {
      ty += lc.dividerGapNoBars
    }

    if (cfg.layout === 'stacked') {
      // Stacked: PROS section then CONS section, each full-width
      if (pros.length > 0) {
        ctx.font = lc.sectionFont
        ctx.save()
        ctx.letterSpacing = `${SECTION_LETTER_SPACING}px`
        ctx.fillStyle = '#15803d'
        ctx.fillText('PROS', leftX, ty + lc.sectionHeaderH - 5)
        ctx.restore()
        ty += lc.sectionHeaderH

        for (const pro of pros) {
          ctx.fillStyle = '#166534'
          ctx.font =
            pro.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
          const lines = wrapText(
            ctx,
            pro.label,
            textWidth - lc.bulletOff - lc.bulletIndent,
          )
          for (let j = 0; j < lines.length; j++) {
            ty += lc.itemLineH
            if (j === 0) ctx.fillText('+', leftX + lc.bulletOff, ty)
            ctx.fillText(lines[j], leftX + lc.bulletOff + lc.bulletIndent, ty)
          }
          ty += lc.itemBottomM
        }
      }

      if (cons.length > 0) {
        if (pros.length > 0) ty += lc.sectionGap
        ctx.font = lc.sectionFont
        ctx.save()
        ctx.letterSpacing = `${SECTION_LETTER_SPACING}px`
        ctx.fillStyle = '#b91c1c'
        ctx.fillText('CONS', leftX, ty + lc.sectionHeaderH - 5)
        ctx.restore()
        ty += lc.sectionHeaderH

        for (const con of cons) {
          ctx.fillStyle = '#991b1b'
          ctx.font =
            con.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
          const lines = wrapText(
            ctx,
            con.label,
            textWidth - lc.bulletOff - lc.bulletIndent,
          )
          for (let j = 0; j < lines.length; j++) {
            ty += lc.itemLineH
            if (j === 0) ctx.fillText('−', leftX + lc.bulletOff, ty)
            ctx.fillText(lines[j], leftX + lc.bulletOff + lc.bulletIndent, ty)
          }
          ty += lc.itemBottomM
        }
      }
    } else {
      // Two-column: PROS left, CONS right
      const colWidth = (textWidth - lc.colGap) / 2
      const rightX = leftX + colWidth + lc.colGap

      // Vertical divider
      const midX = leftX + colWidth + lc.colGap / 2
      const dividerBottom = y + h - lc.cardPadding
      ctx.strokeStyle = '#e2e8f0'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(midX, ty - 4)
      ctx.lineTo(midX, dividerBottom)
      ctx.stroke()

      // Column headers
      ctx.font = lc.sectionFont
      ctx.save()
      ctx.letterSpacing = `${SECTION_LETTER_SPACING}px`
      ctx.fillStyle = '#15803d'
      ctx.fillText('PROS', leftX, ty + lc.sectionHeaderH - 5)
      ctx.fillStyle = '#b91c1c'
      ctx.fillText('CONS', rightX, ty + lc.sectionHeaderH - 5)
      ctx.restore()

      let prosY = ty + lc.sectionHeaderH
      let consY = ty + lc.sectionHeaderH

      for (const pro of pros) {
        ctx.fillStyle = '#166534'
        ctx.font =
          pro.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
        const lines = wrapText(
          ctx,
          pro.label,
          colWidth - lc.bulletOff - lc.bulletIndent,
        )
        for (let j = 0; j < lines.length; j++) {
          prosY += lc.itemLineH
          if (j === 0) ctx.fillText('+', leftX + lc.bulletOff, prosY)
          ctx.fillText(lines[j], leftX + lc.bulletOff + lc.bulletIndent, prosY)
        }
        prosY += lc.itemBottomM
      }

      for (const con of cons) {
        ctx.fillStyle = '#991b1b'
        ctx.font =
          con.weight === WeightEnum.HIGH ? lc.itemFontBold : lc.itemFont
        const lines = wrapText(
          ctx,
          con.label,
          colWidth - lc.bulletOff - lc.bulletIndent,
        )
        for (let j = 0; j < lines.length; j++) {
          consY += lc.itemLineH
          if (j === 0) ctx.fillText('−', rightX + lc.bulletOff, consY)
          ctx.fillText(lines[j], rightX + lc.bulletOff + lc.bulletIndent, consY)
        }
        consY += lc.itemBottomM
      }
    }

    cursorY += h + lc.cardGap
  })

  // Branding footer
  const footerCenterY = cursorY - lc.cardGap + lc.footerGap + lc.footerH / 2
  const footerText = 'Created at pros-cons-list.com'

  ctx.font = lc.footerFont
  const textMeasure = ctx.measureText(footerText).width
  const totalFooterWidth = logo
    ? lc.logoSize + lc.logoTextGap + textMeasure
    : textMeasure
  const footerStartX = (CANVAS_WIDTH - totalFooterWidth) / 2

  if (logo) {
    ctx.drawImage(
      logo,
      footerStartX,
      footerCenterY - lc.logoSize / 2,
      lc.logoSize,
      lc.logoSize,
    )
  }

  ctx.fillStyle = '#94a3b8'
  ctx.fillText(
    footerText,
    footerStartX + (logo ? lc.logoSize + lc.logoTextGap : 0),
    footerCenterY + 4,
  )

  ctx.restore()

  canvas.toBlob((blob) => {
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${fileName || 'pros-cons-list'}.png`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 'image/png')
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}
